import { inject, Injectable } from '@angular/core';
import { GeocodingProvider, ORG_SETTINGS_ID, OrgSettings } from '../models/org-settings.model';
import { AuthService } from './auth.service';
import { ClockSyncService } from './clock-sync.service';
import { DatabaseService, type QuerySignal } from './database.service';

/**
 * Org-level feature toggles (currently just the leaderboard). Freely client-writable,
 * unlike the server-authoritative `organization` table — see org-settings.model.ts.
 * The singleton row is seeded by customer.sql, so `getOrCreate` below is a defensive
 * fallback only, not the normal path.
 */
@Injectable({
  providedIn: 'root',
})
export class OrgSettingsService {
  private db = inject(DatabaseService);
  private clockSync = inject(ClockSyncService);
  private authService = inject(AuthService);

  createQuery(): QuerySignal<OrgSettings | null> {
    return this.db.createQuery('org_settings', () => this.get());
  }

  async get(): Promise<OrgSettings | null> {
    await this.db.init();
    const rows = await this.db.exec('SELECT * FROM org_settings WHERE id = ?', [ORG_SETTINGS_ID]);
    return rows.length > 0 ? (rows[0] as OrgSettings) : null;
  }

  async isLeaderboardEnabled(): Promise<boolean> {
    const settings = await this.get();
    return settings ? settings.leaderboard_enabled === 1 : true; // default-on if row is somehow missing
  }

  /** Empty array means unrestricted (no country filter applied to map search). */
  async getSearchCountryCodes(): Promise<string[]> {
    const settings = await this.get();
    const raw = settings?.search_country_codes ?? '';
    return raw
      .split(',')
      .map((code) => code.trim())
      .filter((code) => code.length > 0);
  }

  /** Which service backs BOTH map address search and pin-drop reverse geocoding. */
  async getGeocodingProvider(): Promise<GeocodingProvider> {
    const settings = await this.get();
    return settings?.geocoding_provider ?? 'here';
  }

  async setGeocodingProvider(provider: GeocodingProvider): Promise<void> {
    const userId = this.authService.getCurrentUserId();
    if (!userId) throw new Error('User must be authenticated to change org settings');
    await this.db.init();
    const now = this.clockSync.now();
    await this.db.run(
      'UPDATE org_settings SET geocoding_provider = ?, updated_by = ?, updated_at = ? WHERE id = ?',
      [provider, userId, now, ORG_SETTINGS_ID],
    );
  }

  async setLeaderboardEnabled(enabled: boolean): Promise<void> {
    const userId = this.authService.getCurrentUserId();
    if (!userId) throw new Error('User must be authenticated to change org settings');
    await this.db.init();
    const now = this.clockSync.now();
    await this.db.run(
      'UPDATE org_settings SET leaderboard_enabled = ?, updated_by = ?, updated_at = ? WHERE id = ?',
      [enabled ? 1 : 0, userId, now, ORG_SETTINGS_ID],
    );
  }
}
